import type { ForwardRefRenderFunction, PropsWithoutRef, ConsumerProps, Context } from 'react';

import { forwardRef as reactForwardRef, memo as reactMemo, createContext, useContext } from 'react';

type DefaultValue = Symbol;

const defaultValue = Symbol('context default value');

/**
 * Create a safe context.
 */
export function createSafeContext<ContextValue>() {
  return createContext<ContextValue | DefaultValue>(defaultValue);
}

/**
 * Use the context. Auto throws error if provider is not found
 * @param TheContext
 */
export function useSafeContext<T>(TheContext: Context<T | DefaultValue>) {
  const value = useContext(TheContext);
  if (value === defaultValue) {
    throw new Error('No value provided for context, you might forgot to wrap the Provider');
  }
  return value as T;
}

/**
 * Create a consumer, uses safe context
 * @param TheContext
 */
export function createSafeConsumer<T>(TheContext: Context<T | DefaultValue>) {
  return function SafeConsumer({ children }: ConsumerProps<T>) {
    const value = useSafeContext(TheContext);
    return children(value);
  };
}

export function forwardedRef<T, P>(
  Component: ForwardRefRenderFunction<T, P>,
  displayName: string,
  memoComponent?: false,
): ReturnType<typeof reactForwardRef<T, P>>;

export function forwardedRef<T, P>(
  Component: ForwardRefRenderFunction<T, P>,
  displayName: string,
  memoComponent: true,
): ReturnType<typeof reactMemo<ReturnType<typeof reactForwardRef<T, P>>>>;

export function forwardedRef<T, P>(
  Component: ForwardRefRenderFunction<T, PropsWithoutRef<P>>,
  displayName: string,
  memoComponent = false,
) {
  const forwardedComponent = reactForwardRef<T, P>(Component);
  forwardedComponent.displayName = `ForwardRef(${displayName})`;

  if (!memoComponent) {
    return forwardedComponent;
  }

  const memoizedComponent = reactMemo(forwardedComponent);
  memoizedComponent.displayName = `Memo(${displayName})`;

  return memoizedComponent;
}
